/**
 * Plot pick geometry: lat/lon in and out of plot-local metres, the surveyed
 * cell under a screen point off the drawn leaves, ground under a plot point
 * in body-fixed metres, and a plot point's own place on the pane.
 */
import * as THREE from 'three';

import { isOnScreen, metresPerPixel } from './camera.ts';
import type { PickHandle } from './pick';
import { insideHexZ, type PlotMeta, type SubPlot } from './plotField.ts';
import { isPyramidPlot, patchLatLon, patchXZ } from './pyramidPlot.ts';
import { polarFrameOf, polarRegionLatLon, polarRegionXZ } from './regionFrame.ts';
import type { PyramidViewHandle } from './pyramidView';
import type { AtlasLive } from '../atlas';
import type { MountHandle } from './mount';
import type { SolarScrubHandle } from './solarScrub';
import type { BodyReadersHandle } from './bodyReaders';
import type { SurfaceRadiusHandle } from './surfaceRadius';
import type { BodySceneHandle } from './bodyScene';
import type { InjectsHandle } from './injects';

export interface PlotGeometryDeps {
  readonly mount: MountHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly bodyScene: BodySceneHandle;
  readonly injects: InjectsHandle;
  readonly live: AtlasLive;
  surfaceRegime(): boolean;
  readonly pick: PickHandle;
}

export interface PlotGeometryHandle {
  latLonAt(xM: number, zM: number): { lat: number; lon: number };
  selectedCellLatLon(): { lat: number; lon: number } | null;
  regionXZAt(lat: number, lon: number): { x: number; z: number } | null;
  subPlotAt(x: number, z: number): SubPlot | null;
  pickSubPlotOnTiles(): SubPlot | null;
  plotGroundLocal(xM: number, zM: number, out: THREE.Vector3): THREE.Vector3 | null;
  plotPointToScreen(
    xM: number, zM: number,
  ): { clientX: number; clientY: number; onScreen: boolean } | null;
}

export function createPlotGeometry(deps: PlotGeometryDeps): PlotGeometryHandle {
  const { pyramidView, surfaceRegime } = deps;
  const { camera, cameraWorld, surface } = deps.mount;
  const { solar } = deps.solarScrub;
  const { registryDatumM, ladderBody } = deps.bodyReaders;
  const { surfaceWorld, leafDatumM } = deps.surfaceRadius;

  /**
   * Real lat/lon for a point in plot-local metres.
   *
   * Every plot already knows where it is — the region records its centre and
   * each hex its offset — so nothing has to be re-baked to give plots
   * coordinates. And coordinates are what a NAME can be derived from.
   */
  function latLonAt(xM: number, zM: number): { lat: number; lon: number } {
    const m = deps.live.plotField!.meta;
    if (isPyramidPlot(m)) return patchLatLon(m.center_lat_deg, m.center_lon_deg, leafDatumM(), xM, zM);
    const polar = polarFrameOf(m.frame);
    if (polar) return polarRegionLatLon(polar, m.center_lat_deg, m.center_lon_deg, xM, zM);
    const f = regionFrameScale(m);
    const lat = m.center_lat_deg - zM / f.lat;
    const lon = m.center_lon_deg + xM / f.lon;
    return { lat, lon: ((lon % 360) + 360) % 360 };
  }

  /**
   * The selected cell's own lat/lon, for a framing that should land on it.
   *
   * Null with nothing selected, or with nothing to convert against, which hands
   * the framing back to `descentAnchor`.
   */
  function selectedCellLatLon(): { lat: number; lon: number } | null {
    if (!deps.live.selectedSub || !deps.live.plotField || deps.injects.state.selectEnterOffCentre) return null;
    return latLonAt(deps.live.selectedSub.x, deps.live.selectedSub.z);
  }

  /**
   * Metres per degree on the region's own grid, latitude and longitude.
   *
   * The bake's frame is the PRODUCT's equirectangular grid, whose longitude
   * scale is a constant cos(the product's CENTER_LATITUDE) - not cos(the
   * point's own latitude, which a sphere gives. Jezero's product is centred at
   * 15 degrees and the site sits at 18.4, so the two are 1.8% apart: 96 m of
   * longitude at the region's east edge, measured by
   * `tools/dtm/region-tiles-check.py`'s frame reading. `meta.frame` is the
   * bake's own pair; the spherical derivation is the fallback for a region
   * baked before it was written.
   */
  function regionFrameScale(m: PlotMeta): { lat: number; lon: number } {
    const f = m.frame;
    if (f) return { lat: f.m_per_deg_lat, lon: f.m_per_deg_lon };
    const perDeg = (registryDatumM(ladderBody()) * Math.PI) / 180;
    return { lat: perDeg, lon: perDeg * Math.cos((m.center_lat_deg * Math.PI) / 180) };
  }

  const _pick = new THREE.Vector4();

  /**
   * Plot-local metres for a real lat/lon: `latLonAt` the other way round.
   *
   * The same planar region frame `src/atlas/siteAlbedo.ts` maps the orthoimage
   * through - +x east, +z south, the site centre at the origin, on the bake's
   * own metres per degree - so a point on a leaf and a point on the region
   * image agree.
   */
  function regionXZAt(lat: number, lon: number): { x: number; z: number } | null {
    const m = deps.live.plotField?.meta;
    if (!m) return null;
    if (isPyramidPlot(m)) return patchXZ(m.center_lat_deg, m.center_lon_deg, leafDatumM(), lat, lon);
    const polar = polarFrameOf(m.frame);
    if (polar) return polarRegionXZ(polar, m.center_lat_deg, m.center_lon_deg, lat, lon);
    const f = regionFrameScale(m);
    const dLon = (((lon - m.center_lon_deg) % 360) + 540) % 360 - 180;
    return { x: dLon * f.lon, z: (m.center_lat_deg - lat) * f.lat };
  }

  /** The surveyed cell containing a point in region-local metres. */
  function subPlotAt(x: number, z: number): SubPlot | null {
    for (const s of deps.live.plotSubs) {
      if (insideHexZ(x - s.x, z - s.z, s.radius_m)) return s;
    }
    return null;
  }

  /**
   * The surveyed cell under the cursor, picked off the drawn leaves.
   *
   * Not a raycast: the whole-file mesh is not in the scene above the SURFACE
   * edge and the leaves are hundreds of separate meshes. Each cell's centre is
   * projected forward - its lat/lon off the region frame, its height off the
   * leaf under it - and the nearest one inside its own footprint in pixels
   * wins.
   */
  function pickSubPlotOnTiles(): SubPlot | null {
    const node = deps.bodyScene.state.bodyNode;
    if (!deps.live.plotSubs.length || !node) return null;
    const rect = surface.getBoundingClientRect();
    const clickX = (deps.pick.ndc.x * 0.5 + 0.5) * rect.width;
    const clickY = (-deps.pick.ndc.y * 0.5 + 0.5) * rect.height;
    camera.updateMatrixWorld();
    let best: SubPlot | null = null;
    let bestPx = Infinity;
    let bestRadiusPx = 0;
    for (const sub of deps.live.plotSubs) {
      const ll = latLonAt(sub.x, sub.z);
      const h = pyramidView.tileHeightAt(ll.lat, ll.lon);
      const w = surfaceWorld(node, ll.lat, ll.lon, leafDatumM() + (h ?? 0) * solar.exag);
      _pick.set(w.x - cameraWorld.x, w.y - cameraWorld.y, w.z - cameraWorld.z, 1);
      const dist = Math.hypot(_pick.x, _pick.y, _pick.z);
      _pick.applyMatrix4(camera.matrixWorldInverse);
      // View-space z before NDC: at Atlas scale the hyperbolic z mapping cannot
      // tell a point in front of the eye from one behind it (src/atlas/camera.ts).
      if (_pick.z >= -camera.near) continue;
      _pick.applyMatrix4(camera.projectionMatrix);
      const px = (_pick.x / _pick.w * 0.5 + 0.5) * rect.width;
      const py = (-_pick.y / _pick.w * 0.5 + 0.5) * rect.height;
      const d = Math.hypot(px - clickX, py - clickY);
      if (d >= bestPx) continue;
      bestPx = d;
      best = sub;
      bestRadiusPx = sub.radius_m / metresPerPixel(dist, camera.fov, Math.max(1, rect.height));
    }
    // Inside the cell's own footprint, in pixels, so a click on empty ground
    // beside the claim clears the selection instead of snapping to its edge.
    return best && bestPx <= bestRadiusPx ? best : null;
  }

  /**
   * A point of region-local ground in the body-fixed metres the LEAVES carry.
   *
   * `tileGroup`'s own frame: `src/atlas/tileMesh.ts` builds every leaf on
   * `latLonToVec3`'s axes about the body centre, and the group carries the
   * body's rotation and world position, so anything drawn as its child sits on
   * the ground the leaves draw without a frame of its own.
   */
  function plotGroundLocal(xM: number, zM: number, out: THREE.Vector3): THREE.Vector3 | null {
    if (!deps.live.plotField) return null;
    const ll = latLonAt(xM, zM);
    // The pick's own fallback where no leaf is resident yet, so the mark lands
    // where `pickSubPlotOnTiles` believes the cell is.
    const r = leafDatumM() + (pyramidView.tileHeightAt(ll.lat, ll.lon) ?? 0) * solar.exag;
    const lat = THREE.MathUtils.degToRad(ll.lat);
    const lon = THREE.MathUtils.degToRad(ll.lon);
    return out.set(
      r * Math.cos(lat) * Math.cos(lon),
      r * Math.sin(lat),
      -r * Math.cos(lat) * Math.sin(lon),
    );
  }

  const _screen = new THREE.Vector3();

  /**
   * Where a point of region-local ground lands on the pane, CSS pixels.
   *
   * Whichever field is drawn under it: the whole-file mesh inside the
   * walkaround, the resident leaves above it. `--select` reads the cell
   * outlines through this and decides containment itself, so what it calls the
   * cell under a pixel owes nothing to `pickSubPlotOnTiles`.
   */
  function plotPointToScreen(
    xM: number, zM: number,
  ): { clientX: number; clientY: number; onScreen: boolean } | null {
    const rect = surface.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0) || !deps.live.plotField) return null;
    camera.updateMatrixWorld();
    if (surfaceRegime() && deps.live.plot) {
      const y = (deps.live.plotField.heightAt(xM, zM) - deps.live.plotField.meta.elevation.min_m) * solar.exag;
      _screen.set(xM, y, zM);
      deps.live.plot.group.updateMatrixWorld();
      deps.live.plot.group.localToWorld(_screen);
    } else {
      const node = deps.bodyScene.state.bodyNode;
      if (!node || !plotGroundLocal(xM, zM, _screen)) return null;
      _screen.applyQuaternion(node.quaternion);
      _screen.set(
        node.world.x + _screen.x - cameraWorld.x,
        node.world.y + _screen.y - cameraWorld.y,
        node.world.z + _screen.z - cameraWorld.z,
      );
    }
    const onScreen = isOnScreen(camera, _screen);
    _screen.project(camera);
    return {
      clientX: rect.left + (_screen.x * 0.5 + 0.5) * rect.width,
      clientY: rect.top + (-_screen.y * 0.5 + 0.5) * rect.height,
      onScreen,
    };
  }

  return {
    latLonAt,
    selectedCellLatLon,
    regionXZAt,
    subPlotAt,
    pickSubPlotOnTiles,
    plotGroundLocal,
    plotPointToScreen,
  };
}
